import React, { createContext, useContext, useState, useCallback } from 'react';
import { DEMO_POSTS, DEMO_COMMENTS, DEMO_USER } from '../data/demoData';

const DemoContext = createContext();

export const useDemo = () => useContext(DemoContext);

export const DemoProvider = ({ children }) => {
    const [isDemoMode, setIsDemoMode] = useState(
        () => localStorage.getItem('isDemoMode') === 'true'
    );
    const [posts, setPosts] = useState(() =>
        localStorage.getItem('isDemoMode') === 'true' ? [...DEMO_POSTS] : []
    );
    const [comments, setComments] = useState(() =>
        localStorage.getItem('isDemoMode') === 'true'
            ? Object.fromEntries(
                  Object.entries(DEMO_COMMENTS).map(([k, v]) => [k, [...v]])
              )
            : {}
    );
    const [likedPostIds, setLikedPostIds] = useState([]);
    const [likedCommentIds, setLikedCommentIds] = useState([]);

    const enterDemoMode = useCallback(() => {
        localStorage.setItem('isDemoMode', 'true');
        localStorage.setItem('isLoggedIn', 'true');
        setIsDemoMode(true);
        setPosts([...DEMO_POSTS]);
        setComments(
            Object.fromEntries(
                Object.entries(DEMO_COMMENTS).map(([k, v]) => [k, [...v]])
            )
        );
        setLikedPostIds([]);
        setLikedCommentIds([]);
    }, []);

    const exitDemoMode = useCallback(() => {
        localStorage.removeItem('isDemoMode');
        localStorage.setItem('isLoggedIn', 'false');
        localStorage.removeItem('tokenExpiresAt');
        setIsDemoMode(false);
        setPosts([]);
        setComments({});
        setLikedPostIds([]);
        setLikedCommentIds([]);
    }, []);

    const likePost = useCallback((postId) => {
        setLikedPostIds((prev) => {
            const isLiked = prev.includes(postId);
            const next = isLiked ? prev.filter((id) => id !== postId) : [...prev, postId];
            setPosts((prevPosts) =>
                prevPosts.map((p) =>
                    p.id === postId
                        ? { ...p, likesCount: p.likesCount + (isLiked ? -1 : 1) }
                        : p
                )
            );
            return next;
        });
    }, []);

    const likeComment = useCallback((commentId) => {
        setLikedCommentIds((prev) => {
            const isLiked = prev.includes(commentId);
            const next = isLiked ? prev.filter((id) => id !== commentId) : [...prev, commentId];
            setComments((prevComments) => {
                const updated = { ...prevComments };
                for (const postId of Object.keys(updated)) {
                    updated[postId] = updated[postId].map((c) =>
                        c.id === commentId
                            ? { ...c, likesCount: c.likesCount + (isLiked ? -1 : 1) }
                            : c
                    );
                }
                return updated;
            });
            return next;
        });
    }, []);

    const addPost = useCallback((content) => {
        const newPost = {
            id: `demo-post-new-${Date.now()}`,
            content,
            authorId: DEMO_USER.id,
            authorUsername: DEMO_USER.username,
            authorIconUrl: null,
            createdAt: new Date().toISOString(),
            likesCount: 0,
            commentsCount: 0,
        };
        setPosts((prev) => [newPost, ...prev]);
        return newPost;
    }, []);

    const addComment = useCallback((postId, content) => {
        const newComment = {
            id: `demo-comment-new-${Date.now()}`,
            parentId: postId,
            content,
            authorId: DEMO_USER.id,
            authorUsername: DEMO_USER.username,
            authorIconUrl: null,
            createdAt: new Date().toISOString(),
            likesCount: 0,
        };
        setComments((prev) => ({
            ...prev,
            [postId]: [...(prev[postId] || []), newComment],
        }));
        setPosts((prev) =>
            prev.map((p) =>
                p.id === postId ? { ...p, commentsCount: p.commentsCount + 1 } : p
            )
        );
        return newComment;
    }, []);

    const getDemoPost = useCallback(
        (postId) => posts.find((p) => p.id === postId) || null,
        [posts]
    );

    const getDemoComments = useCallback(
        (postId) => comments[postId] || [],
        [comments]
    );

    const value = {
        isDemoMode,
        demoUser: DEMO_USER,
        demoPosts: posts,
        demoLikedPostIds: likedPostIds,
        demoLikedCommentIds: likedCommentIds,
        enterDemoMode,
        exitDemoMode,
        likePost,
        likeComment,
        addPost,
        addComment,
        getDemoPost,
        getDemoComments,
    };

    return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
};

export default DemoContext;
